import { useCallback, useEffect, useMemo, useState } from "react";
import {
  GeneralInfoSection,
  ScheduleSection,
  AdsTextConfigurationSection,
  ImagesSection,
  VideosSection,
} from 'components';
import { useGoogleAds, useMetaAds } from "hooks";

const CampaignDetailsTab = ({ data, editMode = false, onDataChange, platform, loading }) => {
  const { activeAccount: activeGoogleAccount } = useGoogleAds();
  const { activeAccount: activeMetaAccount } = useMetaAds();
  const account = data.provider === "meta" ? activeMetaAccount : activeGoogleAccount;
  const currency = account?.currency || "";

  const adContentList = useMemo(() => Array.isArray(data.ad_content) ? data.ad_content : [data.ad_content], [data.ad_content]);

  const formatDate = date => date ? new Date(date).toLocaleDateString() : "Sin definir";

  const initializeDailyBudget = useCallback(campaignData => {
    if (typeof campaignData.daily_budget === 'number' || (typeof campaignData.daily_budget === 'string' && campaignData.daily_budget !== "")) {
      return campaignData.daily_budget;
    }
    if (Array.isArray(campaignData.ad_groups) && campaignData.ad_groups.length > 0) {
      const adsetBudgets = {};
      for (const adGroup of campaignData.ad_groups) {
        if (adGroup.id && (typeof adGroup.daily_budget === 'number' || (typeof adGroup.daily_budget === 'string' && adGroup.daily_budget !== ""))) {
          adsetBudgets[adGroup.id] = adGroup.daily_budget;
        }
      }
      return Object.keys(adsetBudgets).length > 0 ? adsetBudgets : "";
    }
    return "";
  }, []);

  const mapAdFieldsById = useCallback((primaryField, fallbackField) => {
    const result = {};
    for (const ad of adContentList) {
      if (!ad || !ad.id) continue;
      const adId = data.campaign_type === "PERFORMANCE_MAX" ? "performance_max" : ad.id;
      result[adId] = ad[primaryField] || (ad[fallbackField] ? [ad[fallbackField]] : []);
    }
    return result;
  }, [adContentList, data.campaign_type]);

  const collectUniqueAssets = extractor => [...new Set(adContentList.flatMap(ad => extractor(ad) || []))];

  const getInitialEditableData = useCallback(() => ({
    name: data.name || data.campaign_name || "",
    start_date: data.start_date || "",
    end_date: data.end_date || "",
    daily_budget: initializeDailyBudget(data),
    headlines: mapAdFieldsById("headlines", "name"),
    descriptions: (() => {
      const result = {};
      for (const ad of adContentList) {
        if (!ad || !ad.id) continue;
        const adId = data.campaign_type === "PERFORMANCE_MAX" ? "performance_max" : ad.id;
        const descriptionDependingOfPlatform = platform === "meta" ? ad.body : ad.descriptions;
        result[adId] = [...new Set((descriptionDependingOfPlatform || []).filter(Boolean))];
      }
      return result;
    })(),
    keywords: data.campaign_type === "SEARCH"
      ? data.ad_groups.reduce((acc, group) => {
          const kws = group.ads?.flatMap(ad => ad.keywords || []) || [];
          acc[group.id] = [...new Set(kws.filter(Boolean))];
          return acc;
        }, {})
      : {},
  }), [data, adContentList, platform, mapAdFieldsById, initializeDailyBudget]);

  const [editableData, setEditableData] = useState(() => getInitialEditableData());
  const [originalData, setOriginalData] = useState(() => getInitialEditableData());
  const [changedFields, setChangedFields] = useState(new Set());
  const [assetsUpdates, setAssetsUpdates] = useState({});
  const [fileMap, setFileMap] = useState({});

  useEffect(() => {
    const initialData = getInitialEditableData();
    setEditableData(initialData);
    setOriginalData(initialData);
    setChangedFields(new Set());
    setAssetsUpdates({});
    setFileMap({});
  }, [data, editMode, getInitialEditableData]);

  const deepEqual = (a, b) => {
    if (a === b) return true;
    if (a == null || b == null || typeof a !== typeof b) return false;
    if (typeof a === 'object') {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length) return false;
      for (const key of keysA) {
        if (!keysB.includes(key) || !deepEqual(a[key], b[key])) return false;
      }
      return true;
    }
    return false;
  };

  const getModifiedAdContent = (originalMap, editedMap) => {
    const changedMap = {};
    for (const adId of Object.keys(editedMap)) {
      const original = originalMap[adId] || [];
      const edited = editedMap[adId] || [];
      if (!deepEqual(original, edited)) {
        changedMap[adId] = edited;
      }
    }
    return Object.keys(changedMap).length > 0 ? changedMap : null;
  };

  const sendUpdates = (dataToSend = editableData, changedToSend = changedFields) => {
    const textualChanges = {}
    for (const field of changedToSend) {
      if (field === 'headlines' || field === 'descriptions') {
        const changedMap = getModifiedAdContent(originalData[field], dataToSend[field])
        if (changedMap) textualChanges[field] = changedMap
      } else {
        textualChanges[field] = dataToSend[field]
      }
    }
    const hasTextualChanges = Object.keys(textualChanges).length > 0
    const hasAssetsChanges = Object.keys(assetsUpdates).length > 0
    const hasFiles = Object.keys(fileMap).length > 0
    if (!hasTextualChanges && !hasAssetsChanges) return
    const fullUpdatePayload = { ...textualChanges }
    if (hasAssetsChanges) {
      const assetsStructure = {}
      for (const [adId, adAssets] of Object.entries(assetsUpdates)) {
        assetsStructure[adId] = { assets: adAssets }
      }
      fullUpdatePayload.assets = assetsStructure
    }
    if (hasFiles) {
      const formData = new FormData()
      formData.append('updates', JSON.stringify(fullUpdatePayload))
      for (const [placeholder, file] of Object.entries(fileMap)) {
        formData.append(placeholder, file)
      }
      onDataChange?.({
        type: 'multipart_update',
        payload: formData,
        isMultipart: true,
      })
    } else {
      onDataChange?.({
        type: 'update',
        payload: fullUpdatePayload,
        isMultipart: false,
      })
    }
  }
  
  const handleFieldChange = (fieldName, newValue) => {
    const updatedData = { ...editableData, [fieldName]: newValue }
    const updatedChangedFields = new Set(changedFields)
    if (fieldName === 'headlines' || fieldName === 'descriptions') {
      const changedAdContent = getModifiedAdContent(originalData[fieldName], newValue)
      if (changedAdContent) updatedChangedFields.add(fieldName)
      else updatedChangedFields.delete(fieldName)
    } else {
      if (!deepEqual(originalData[fieldName], newValue)) updatedChangedFields.add(fieldName)
      else updatedChangedFields.delete(fieldName)
    }
    setEditableData(updatedData)
    setChangedFields(updatedChangedFields)
    sendUpdates(updatedData, updatedChangedFields)
  }
  
  
  const handleMediaFormDataChange = ({ assetsUpdates: newAssetsUpdates, fileMap: newFileMap }) => {
    const mergedAssets = { ...assetsUpdates, ...newAssetsUpdates };
    const mergedFiles = { ...fileMap, ...newFileMap };
  
    setAssetsUpdates(mergedAssets);
    setFileMap(mergedFiles);
  
    const textualChanges = {};
    for (const field of changedFields) {
      if (field === "headlines" || field === "descriptions") {
        const changedMap = getModifiedAdContent(originalData[field], editableData[field]);
        if (changedMap) textualChanges[field] = changedMap;
      } else {
        textualChanges[field] = editableData[field];
      }
    }
  
    const fullUpdatePayload = { ...textualChanges };
    if (Object.keys(mergedAssets).length > 0) {
      fullUpdatePayload.assets = mergedAssets;
    }
  
    const hasFiles = Object.keys(mergedFiles).length > 0;
  
    if (hasFiles) {
      const formData = new FormData();
      formData.append('updates', JSON.stringify(fullUpdatePayload));
      Object.entries(mergedFiles).forEach(([placeholder, file]) => {
        formData.append(placeholder, file);
      });
  
      onDataChange?.({
        type: 'multipart_update',
        payload: formData,
        isMultipart: true,
      });
    } else {
      onDataChange?.({
        type: 'update',
        payload: fullUpdatePayload,
        isMultipart: false,
      });
    }
  };

  const handleAdGroupBudgetChange = (adGroupId, value) => {
    const newBudget = value === "" ? "" : Math.max(0, Math.floor(Number(value)));
    const updatedBudgets = { ...(editableData.daily_budget || {}), [adGroupId]: Number(newBudget) };
    handleFieldChange("daily_budget", updatedBudgets);
  };

  const allImageUrls = collectUniqueAssets(ad => ad?.image_urls);
  const allVideoUrls = collectUniqueAssets(ad => ad?.video_urls || []);

  return (
    <div className="bg-transparent">
      <div className="space-y-6 max-h-[70vh] overflow-auto pr-2">
        <GeneralInfoSection
          data={data}
          editableData={editableData}
          editMode={editMode}
          onChange={handleFieldChange}
          currency={currency}
          platform={platform}
          loading={loading}
        />

        <ScheduleSection
          data={data}
          editableData={editableData}
          editMode={editMode}
          onChange={handleFieldChange}
          formatDate={formatDate}
          loading={loading}
        />

        <AdsTextConfigurationSection
          data={data}
          platform={platform}
          editableData={editableData}
          editMode={editMode}
          onChange={handleFieldChange}
          onAdsetBudgetChange={handleAdGroupBudgetChange}
          loading={loading}
        />

        {allImageUrls.length > 0 && (
          <ImagesSection
            data={data}
            editMode={editMode}
            onDataChange={handleMediaFormDataChange}
            platform={platform}
          />
        )}

        {allVideoUrls.length > 0 && (
          <VideosSection
            data={data}
            editMode={editMode}
            onDataChange={handleMediaFormDataChange}
            platform={platform}
          />
        )}
      </div>
    </div>
  );
};

export default CampaignDetailsTab;